/**
 * accountSecurity.ts — the rules for passwords and for who may keep or lose manager access.
 * Pure functions, used by the Worker (which enforces them) and the admin (which explains them before you submit).
 *
 * Passwords follow NIST SP 800-63B rev 4 and OWASP's Authentication Cheat Sheet: length counts, not "one capital, one
 * symbol" rules; long passphrases and password managers are welcome (up to 128 characters, spaces allowed); the most
 * common passwords and anything built from the person's own name or email are refused. A check against known data
 * breaches runs on the server on top of this (see apps/worker/src/lib/authPolicy.ts).
 */

export const PASSWORD_MIN = 12;
export const PASSWORD_MAX = 128;

/** The passwords every attacker tries first. Not a full list: the breach check on the server covers the rest. */
const COMMON = new Set([
  "password", "password1", "password12", "password123", "passw0rd", "qwerty", "qwertyuiop", "qwerty123", "123456", "1234567",
  "12345678", "123456789", "1234567890", "12345678910", "111111", "000000", "iloveyou", "admin", "administrator", "welcome",
  "welcome1", "letmein", "monkey", "dragon", "football", "baseball", "abc123", "changeme", "changeme123", "default", "login",
  "master", "shadow", "sunshine", "princess", "trustno1", "whatever", "freedom", "starwars", "superman", "batman", "secret",
  "demandhub", "demandhub1", "demandhub123", "demandhubcms", "cmsadmin", "temp1234", "temporary",
]);

const normalise = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, "");

/** `null` when the password is acceptable, else one short sentence saying what to change. */
export function passwordProblem(password: string, who: { email?: string | null; name?: string | null } = {}): string | null {
  if (password.length < PASSWORD_MIN) return `Use at least ${PASSWORD_MIN} characters. A few random words work well.`;
  if (password.length > PASSWORD_MAX) return `Use at most ${PASSWORD_MAX} characters.`;
  const flat = normalise(password);
  // "password1234" and "Welcome2024!" are the common word plus a tail of digits: judge the word.
  const stem = flat.replace(/\d+$/, "");
  if (COMMON.has(flat) || COMMON.has(stem) || COMMON.has(password.toLowerCase())) return "That password is too common. Pick something only you would think of.";
  if (new Set(password).size <= 3) return "That password repeats the same few characters. Pick something less predictable.";
  const local = normalise((who.email ?? "").split("@")[0] ?? "");
  if (local.length >= 4 && flat.includes(local)) return "Do not build the password from your email address.";
  const name = normalise(who.name ?? "");
  if (name.length >= 4 && flat.includes(name)) return "Do not build the password from your name.";
  return null;
}

/** A role change or removal is refused when it would leave nobody able to manage the CMS. */
export function wouldLeaveNoManager(
  users: Array<{ id: string; role: string; isActive: boolean }>,
  change: { userId: string; role?: string; isActive?: boolean; removed?: boolean },
): boolean {
  const after = users
    .filter((u) => !(change.removed && u.id === change.userId))
    .map((u) => (u.id === change.userId ? { ...u, role: change.role ?? u.role, isActive: change.isActive ?? u.isActive } : u));
  return !after.some((u) => u.role === "manager" && u.isActive);
}

/** "Chrome on Mac" from a browser's user-agent text, for the list of signed-in devices. Unknown parts are left out. */
export function describeDevice(userAgent: string | null | undefined): { label: string; mobile: boolean } {
  const ua = userAgent ?? "";
  if (!ua) return { label: "Unknown device", mobile: false };
  const browser = /Edg\//.test(ua) ? "Edge" : /OPR\/|Opera/.test(ua) ? "Opera" : /Firefox\//.test(ua) ? "Firefox" : /Chrome\/|CriOS\//.test(ua) ? "Chrome" : /Safari\//.test(ua) ? "Safari" : null;
  const os = /iPhone/.test(ua) ? "iPhone" : /iPad/.test(ua) ? "iPad" : /Android/.test(ua) ? "Android" : /Windows/.test(ua) ? "Windows" : /Mac OS X|Macintosh/.test(ua) ? "Mac" : /CrOS/.test(ua) ? "Chromebook" : /Linux/.test(ua) ? "Linux" : null;
  const mobile = /iPhone|iPad|Android|Mobile/.test(ua);
  const label = browser && os ? `${browser} on ${os}` : browser ?? os ?? "Unknown device";
  return { label, mobile };
}
